/* The one table the panels use: a thin spec over the design system's `DataTable`. Two things
 * the spec adds that the library has no notion of:
 *
 *   advanced: true   the column is dropped in the simple view
 *   simple: '…'      that column's wording in the simple view
 */

import { useMemo, useState, type ReactNode } from 'react';
import { DataTable, Input } from '@nextleap-al/admin-ui';
import { Search } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import { useApp } from '../state/app';

export interface Column<T> {
  /** Falls back to `key`, then to the column's position. */
  id?: string;
  label: string;
  simple?: string;
  advanced?: boolean;
  num?: boolean;
  key?: Extract<keyof T, string>;
  render?: (row: T) => ReactNode;
  /** What this column sorts on. Defaults to `key`; a column with neither does not sort. */
  sortValue?: (row: T) => string | number | null;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  /** Shown instead of the table when there is nothing, saying what an empty result means
   *  here. */
  empty?: string;
  compact?: boolean;
  /** Rows per page, and the pager that goes with it. Omitted means every row at once. */
  pageSize?: number;
  /** Placeholder for the search box; passing it is what turns the box on. */
  search?: string;
  /** Makes the whole row open whatever it is about. Not reachable by keyboard, so the row
   *  must still carry a real focusable control. */
  onRowClick?: (row: T) => void;
}

/**
 * The text one row can be found by: its own values, whatever the columns sort on, and any
 * cell rendering to a plain string, so "greeting" finds a row stored as `small_talk`. Cells
 * that render to markup are not searchable.
 */
function haystack<T extends object>(row: T, columns: Column<T>[]): string {
  const parts: unknown[] = Object.values(row);

  for (const column of columns) {
    if (column.sortValue) parts.push(column.sortValue(row));
    if (column.render) {
      const cell = column.render(row);
      if (typeof cell === 'string' || typeof cell === 'number') parts.push(cell);
    }
  }

  return parts
    .filter((part) => part != null && typeof part !== 'object')
    .join(' ')
    .toLowerCase();
}

/**
 * Ordering for the page-splitting below. `DataTable` renders exactly the rows it is handed,
 * so sorting has to happen before the slice or a sort would only reorder the current page.
 */
function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function Table<T extends object>({
  columns,
  // A field added to a query can reach the browser before the server is restarted, and
  // `rows.length` on the resulting `undefined` would take down the whole page.
  rows = [],
  empty = 'Nothing recorded in this window.',
  compact = true,
  pageSize,
  search,
  onRowClick,
}: Props<T>) {
  const { simple } = useApp();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize ?? 10);
  const [term, setTerm] = useState('');

  const defs = useMemo<ColumnDef<T, unknown>[]>(() => {
    return columns
      .filter((c) => !(simple && c.advanced))
      .map((c, i) => {
        const id = c.id ?? c.key ?? `col-${i}`;
        const label = (simple && c.simple) || c.label;
        const sortValue = c.sortValue ?? (c.key ? (row: T) => row[c.key!] as string | number : null);

        return {
          id,
          header: () => (
            <span className={c.num ? 'block w-full text-right' : undefined}>{label}</span>
          ),
          ...(sortValue ? { accessorFn: (row: T) => sortValue(row) } : {}),
          enableSorting: Boolean(sortValue),
          cell: ({ row }) => {
            const value = c.render
              ? c.render(row.original)
              : cellText(c.key ? row.original[c.key] : undefined);
            return c.num ? (
              <div className="text-right tabular-nums">{value}</div>
            ) : (
              value
            );
          },
        } satisfies ColumnDef<T, unknown>;
      });
  }, [columns, simple]);

  const needle = term.trim().toLowerCase();

  // Before the sort and the slice, so a search reaches every row rather than the page.
  const found = useMemo(
    () => (needle ? rows.filter((row) => haystack(row, columns).includes(needle)) : rows),
    [rows, columns, needle],
  );

  const active = pageSize ? sorting[0] : undefined;

  const ordered = useMemo(() => {
    if (!active) return found;
    const column = columns.find((c, i) => (c.id ?? c.key ?? `col-${i}`) === active.id);
    const value =
      column?.sortValue ?? (column?.key ? (row: T) => row[column.key!] as string | number : null);
    if (!value) return found;
    const sorted = [...found].sort((a, b) => compare(value(a), value(b)));
    return active.desc ? sorted.reverse() : sorted;
  }, [found, columns, active]);

  // On the right, and only above tables long enough to need it.
  const box = search ? (
    <div className="mb-3 flex justify-end">
      <div className="w-full sm:w-64">
        <Input
          placeholder={search}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
          size="sm"
          aria-label={search}
        />
      </div>
    </div>
  ) : null;

  // Nothing at all is a fact about the window, and there is nothing to search.
  if (!rows.length) {
    return <p className="py-6 text-center text-sm text-[var(--text-muted)]">{empty}</p>;
  }

  // Nothing matching is a different fact, and the box stays so the term can be cleared.
  if (!ordered.length) {
    return (
      <>
        {box}
        <p className="py-6 text-center text-sm text-[var(--text-muted)]">
          {`No row here matches “${term.trim()}”.`}
        </p>
      </>
    );
  }

  if (!pageSize) {
    return (
      <>
        {box}
        <div className="overflow-x-auto">
          <DataTable data={ordered} columns={defs} compact={compact} onRowClick={onRowClick} />
        </div>
      </>
    );
  }

  // Clamped rather than reset in an effect, since a narrower filter can leave the reader on
  // a page that no longer exists.
  const totalPages = Math.max(1, Math.ceil(ordered.length / size));
  const current = Math.min(page, totalPages);

  return (
    <>
      {box}
      <div className="overflow-x-auto">
        <DataTable
          data={ordered.slice((current - 1) * size, current * size)}
          columns={defs}
          compact={compact}
          onRowClick={onRowClick}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={{ page: current, pageSize: size, total: ordered.length, totalPages }}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setSize(next);
            setPage(1);
          }}
        />
      </div>
    </>
  );
}

/** A missing value is an em dash, never an empty cell, which would read as a rendering
 *  fault. */
function cellText(value: unknown): ReactNode {
  if (value == null || value === '') return <Muted>—</Muted>;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-[var(--text-muted)]">{children}</span>;
}

/** Where a line of text stops being a cell and starts being a paragraph. */
const CELL_PREVIEW = 80;

/**
 * A long value cut to its opening words, expanded in place on click. A native `<details>`
 * rather than a dialog, so the text stays beside the columns that give it its meaning.
 */
export function ClampedText({
  text,
  limit = CELL_PREVIEW,
  missing,
}: {
  text: string | null | undefined;
  limit?: number;
  /** What an absent value says, when "—" would be the wrong sentence. */
  missing?: ReactNode;
}) {
  if (!text) return <>{missing ?? <Muted>—</Muted>}</>;
  if (text.length <= limit) return <span className="whitespace-pre-wrap">{text}</span>;
  return (
    <details className="max-w-prose">
      <summary className="cursor-pointer text-[var(--text-secondary)]">
        {`${text.slice(0, limit)}…`}
      </summary>
      <div className="mt-2 whitespace-pre-wrap text-[var(--text-secondary)]">{text}</div>
    </details>
  );
}

/** Longer than a question's preview, since eighty characters clips nearly every reply. */
const ANSWER_PREVIEW = 110;

/** The assistant's reply, inside a table cell. A missing one says "not stored", which is a
 *  fact about the assistant's configuration rather than about that turn. */
export function AnswerCell({ text }: { text: string | null | undefined }) {
  return <ClampedText text={text} limit={ANSWER_PREVIEW} missing={<Muted>not stored</Muted>} />;
}
